import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { BufferGeometryUtils } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import buildDepthMaterial from './scripts/depthMaterial.js'
import buildModifiedMaterial from './scripts/modifiedMaterial.js'
import gsap from 'gsap'

export default class World{
  constructor(){
    // Variables
    this.canvas = document.getElementById('webgl')
    this.scene = new THREE.Scene()
    this.camera
    this.renderer

    // Controls
    this.useOrbit = true
    this.useFly = false
    this.orbitControl
    this.flyHold = false
    this.firstPersonControl
    this.canvasBox = {}
    this.mousePos = {
      x: 0,
      y: 0
    }
    this.cameraRotation
    this.flyMaxSpeed = 0.4
    this.flySpeed = this.flyMaxSpeed/2
    this.gsapAnimations = []

    this.geometry
    
    this.mapSize = 25
    this.positions
    this.mesh
    this.water
    this.uniforms = {
      uTime: {value: 0 },
      uSpeed: { value: 0.15 },
      uFrequency: { value: 0.25 },
      uAmplitude: { value: 10 },
      
      uOffsetX: { value: 0 },
      uOffsetZ: { value: 0 },

      uColor1: { value: new THREE.Color( '#ffffff' ) },
      uColor2: { value: new THREE.Color( '#ffffff' ) },
      uColor3: { value: new THREE.Color( '#ffffff' ) },
      uColor4: { value: new THREE.Color( '#ffffff' ) },
      uEnd1: { value: 0.1 },
      uEnd2: { value: 0.2 },
      uEnd3: { value: 0.5 }
    }
    this.lightDefaultPosition = new THREE.Vector3(-75, 54, 20)
    this.lightTarget
    this.ligtCameraParameters = {
      nearFarCenter: 87.5,
      nearFarAmplitude: 77.5,

      topBottomCenter: 12.5,
      topBottomAmplitude: 62.5,

      rightLeftCenter: 0,
      rightLeftAmplitude: 90
    }

    // Setup
    this.setCamera()
    this.setOrbitControl()
    this.flyEvents()
    this.setRenderer()
    this.setLights()

    this.calculatePositions()
    this.setGeometry()
    this.createMesh()
    this.createWater()
    this.setSky()

    // Loop
    const clock = new THREE.Clock()
    const tick = () => {
      // Update Shader
      this.uniforms.uTime.value = clock.getElapsedTime()

      // Render
      this.renderer.render(this.scene, this.camera)

      // Control
      if(this.useOrbit){
        this.orbitControl.update()
      }
      else if(this.useFly){
        this.rotateFlyCamera()
        if(this.flyHold){
          this.updateFlyUniforms()
        }
      }

      // Loop
      window.requestAnimationFrame(tick)
    }
    tick()

    // Resize
    window.addEventListener('resize', () => {
      this.resize()
    })
    this.resize()

    // Stats
    const loop = setTimeout(() => {
      if(!this.hexagonCount){
        this.hexagonCount = document.getElementById('hexagonCount')
      }
      if(!this.verticesCount){
        this.verticesCount = document.getElementById('verticesCount')
      }
      if(!this.flyControl){
        this.flyControl = document.getElementById('flyControl')
      }
      if(!this.flyValue){
        this.flyValue = document.getElementById('flyValue')
      }

      if(this.hexagonCount && this.verticesCount && this.flyControl && this.flyValue){
        clearInterval(loop)
      }
    }, 100)
  }

  setCamera(){
    this.camera = new THREE.PerspectiveCamera(75, 0, 0.01, -1)
    this.camera.position.set(25, 35, 25)
    this.camera.rotation.reorder('YXZ')
    this.scene.add(this.camera)
  }

  setOrbitControl(){
    this.orbitControl = new OrbitControls(this.camera, this.canvas)
    this.orbitControl.enableDamping = true
    this.orbitControl.screenSpacePanning = false
    this.orbitControl.enablePan = false

    this.orbitControl.maxDistance = 200
    this.orbitControl.maxPolarAngle = Math.PI * 0.4
  }

  setRenderer(){
    this.renderer = new THREE.WebGL1Renderer({
      canvas: this.canvas,
      antialias: true
    })

    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFShadowMap
    this.renderer.physicallyCorrectLights = true

    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.outputEncoding = THREE.sRGBEncoding
  }

  setLights(){    
    this.directionalLight = new THREE.DirectionalLight('#ffffff', 3)

    this.directionalLight.castShadow = true
    
    this.directionalLight.shadow.mapSize.set(1024, 1024)
    this.directionalLight.shadow.normalBias = 0.2
    this.directionalLight.position.copy(this.lightDefaultPosition)
    this.resizeLightCamera()

    this.lightTarget = new THREE.Object3D()
    this.lightTarget.position.set(0, 0, 0)
    this.directionalLight.target = this.lightTarget
    
    this.scene.add(this.lightTarget)
    this.scene.add(this.directionalLight)

    // Ambiant
    const ambiantLight = new THREE.AmbientLight('#ffffff', 0.2)
    this.scene.add(ambiantLight)
  }

  refreshLightPosition(){
    this.directionalLight.position.set(
      this.lightDefaultPosition.x + this.camera.position.x,
      this.lightDefaultPosition.y,
      this.lightDefaultPosition.z + this.camera.position.z
    )
    this.lightTarget.position.set(
      this.camera.position.x,
      0,
      this.camera.position.z
    )
  }

  resizeLightCamera(){
    const ratio = (this.mapSize / 100) + 0.1

    this.directionalLight.shadow.camera.near = this.ligtCameraParameters.nearFarCenter - (this.ligtCameraParameters.nearFarAmplitude * ratio)
    this.directionalLight.shadow.camera.far = this.ligtCameraParameters.nearFarCenter + (this.ligtCameraParameters.nearFarAmplitude * ratio)
    
    this.directionalLight.shadow.camera.bottom = this.ligtCameraParameters.topBottomCenter - (this.ligtCameraParameters.topBottomAmplitude * ratio)
    this.directionalLight.shadow.camera.top = this.ligtCameraParameters.topBottomCenter + (this.ligtCameraParameters.topBottomAmplitude * ratio)

    this.directionalLight.shadow.camera.right = this.ligtCameraParameters.rightLeftCenter - (this.ligtCameraParameters.rightLeftAmplitude * ratio)
    this.directionalLight.shadow.camera.left = this.ligtCameraParameters.rightLeftCenter + (this.ligtCameraParameters.rightLeftAmplitude * ratio)

    this.directionalLight.shadow.camera.updateProjectionMatrix()
  }

  setGeometry(){
    const offset = 1
    const length = this.positions.length
    
    // Update stats
    const tick = setInterval(() => {
      if(this.hexagonCount && this.verticesCount){
        clearInterval(tick)

        this.hexagonCount.innerText = length
        this.verticesCount.innerText = length * 12
      }
    }, 100)

    const buffer = BufferGeometryUtils.mergeBufferGeometries([new THREE.CylinderGeometry( 0.58, 0.58, 10, 6 )])
    const bufferList = []
    const array = new Float32Array(length * 160)

    for(let i = 0; i < length; i++){
      const pos = this.positions[i]
      const newGeometry = buffer.clone()

      // Position
      newGeometry.rotateY(Math.PI * 0.5)
      newGeometry.translate(
        pos[0] * offset, 
        0,
        pos[1] * offset,
      )

      const localId = i * 80
      for (let y = 0; y < 80; y++){
        const id = localId + (y * 2)
        array[id] = pos[0]
        array[id + 1] = pos[1]
      }

      bufferList.push(newGeometry)
    }

    this.geometry = BufferGeometryUtils.mergeBufferGeometries(bufferList)
    this.geometry.setAttribute('aPos', new THREE.BufferAttribute(array, 2)) 
  }

  calculatePositions(){
    // Center
    const positionList =  [[0, 0]]

    // Top Bottom
    for(let i = 0; i < this.mapSize; i++){
      const offsetY = 1 * (i + 1)
      positionList.push([0, offsetY])
      positionList.push([0, -offsetY])
    }

    //Left Right
    let heightCount = this.mapSize * 2
    for(let i = 0; i < this.mapSize; i++){
      const offsetX = 0.9 * (i + 1)
      const offsetY = 0.5 * (heightCount - 1)

      // Build new line
      for (let y = 0; y < heightCount; y++){
        const positionY = -offsetY + heightCount - y - 1
          
        positionList.push([offsetX, positionY])
        positionList.push([-offsetX, positionY])
      }

      heightCount -= 1
    }

    this.positions = positionList
  }

  updateSize(newSize){
    this.mapSize = newSize
    this.resizeLightCamera()
    
    this.calculatePositions()
    this.setGeometry()

    this.mesh.geometry = this.geometry
    this.refreshWater()
  }  

  createWater(){
    this.water = new THREE.Mesh(
      new THREE.CylinderGeometry( 0.5, 0.5, 10, 6 ),
      new THREE.MeshStandardMaterial({
        transparent: true,
        opacity: 0.9,
        color: this.uniforms.uColor1.value
      })
    )

    this.water.receiveShadow = true
    this.water.frustumCulled = false
    this.scene.add(this.water)

    this.refreshWater()
  }

  refreshWater(){
    const xScale = (this.mapSize * 2.2) + 1.3
    const zScale = (this.mapSize * 2.1) + 1.3
    const yScale = this.uniforms.uAmplitude.value * this.uniforms.uEnd1.value

    this.water.position.y = yScale
    this.water.scale.set(
      xScale, 
      1, 
      zScale
    )

    this.water.material.color = this.uniforms.uColor1.value
  }

  createMesh(){
    this.mesh = new THREE.Mesh(
      this.geometry,
      buildModifiedMaterial(this.uniforms),
    )
  
    this.mesh.customDepthMaterial = buildDepthMaterial(this.uniforms)
    this.mesh.castShadow = true
    this.mesh.receiveShadow = true
    this.mesh.frustumCulled = false
    
    this.scene.add(this.mesh)
  }

  resize(){
    const sizes = {
      height: window.innerHeight,
      width: window.innerWidth
    }    

    // Update renderer
    this.renderer.setSize(sizes.width, sizes.height)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.sortObjects = false
    
    // Update camera
    this.camera.aspect = sizes.width / sizes.height
    this.camera.updateProjectionMatrix()

    // Canvas Box
    this.canvasBox = this.canvas.getBoundingClientRect()
  }

  goOrbit(){
    // Kill Animations
    for(let anim of this.gsapAnimations){
      anim.kill()
    }

    this.flyControl.style.opacity = 0

    // Camera Position
    const local = this
    this.useFly = false

    const anim1 = gsap.to(this.camera.position,{
      x: 25 + this.uniforms.uOffsetX.value,
      y: 35,
      z: 25 + this.uniforms.uOffsetZ.value,
      duration: 3,
      ease: "power2.out",
      onComplete:function(){
        local.orbitControl.enabled = true
        local.useOrbit = true
      }
    })

    // Camera rotation
    const anim2 = gsap.to(this.camera.rotation,{
      x: -Math.PI * 0.2485,
      y: Math.PI * 0.25,
      z: 0,
      duration: 3,
      onComplete:function(){
        local.orbitControl.target.set(
          local.uniforms.uOffsetX.value,
          0,
          local.uniforms.uOffsetZ.value
        )
      }
    })

    // Populate Animations
    this.gsapAnimations = [anim1, anim2]
  }

  goFly(){  
    // Kill Animations
    for(let anim of this.gsapAnimations){
      anim.kill()
    }
    
    this.flyValue.innerText = Math.round((this.flySpeed / this.flyMaxSpeed) * 100) / 100
    this.flyControl.style.opacity = 1

    // Animate Camera Position
    const local = this

    this.orbitControl.enabled = false
    this.useOrbit = false
    this.cameraRotation = this.camera.rotation    
    this.uniforms.uSpeed.value = 0

    const anim1 = gsap.to(this.camera.position,{
      x: this.uniforms.uOffsetX.value,
      y: 15 + Number(this.uniforms.uAmplitude.value) + (this.mapSize / 2),
      z: this.uniforms.uOffsetZ.value,
      duration: 2,
      ease: "power2.out",
      onComplete:function(){
        local.useFly = true
      }
    })

    // Populate Animations
    this.gsapAnimations = [anim1]
  }

  flyEvents(){
    // Hold
    this.canvas.addEventListener('mousedown', () => {
      this.flyHold = true
    })
    this.canvas.addEventListener('touchstart', () => {
      this.flyHold = true
    })

    this.canvas.addEventListener('mouseup', () => {
      this.flyHold = false  
    })
    this.canvas.addEventListener('touchend', () => {
      this.flyHold = false  
    })
    document.addEventListener('mouseleave', () => {
      this.flyHold = false
    })

    // Drag
    this.canvas.addEventListener('mousemove', event => {
      if(!this.useOrbit){
        this.flyDrag(event.clientX, event.clientY)
      }
    })
    this.canvas.addEventListener('touchmove', event => {
      if(!this.useOrbit){
        this.flyDrag(event.changedTouches[0].clientX, event.changedTouches[0].clientY)
      }
    })
  }

  flyDrag(x, y){
    const ratioX = (x / this.canvasBox.width) - 0.5
    const ratioY = (y / this.canvasBox.height) - 0.5

    this.mousePos.x = ratioX
    this.mousePos.y = ratioY
  }

  rotateFlyCamera(){
    // Rotate Camera
    this.cameraRotation.x += -this.mousePos.y * 0.02
    this.cameraRotation.z += (-this.mousePos.x - this.cameraRotation.z) * 0.05
    this.cameraRotation.y += this.cameraRotation.z * 0.02

    // Avoid roll
    if(this.cameraRotation.x < -Math.PI/2){
      this.cameraRotation.x = -Math.PI/2
    }
    else if (this.cameraRotation.x > Math.PI/2){
      this.cameraRotation.x = Math.PI/2
    }

    this.camera.rotation.set(this.cameraRotation.x, this.cameraRotation.y, this.cameraRotation.z)
  }

  updateFlyUniforms(){
    // Move Camera
    const direction = new THREE.Vector3()
    this.camera.getWorldDirection(direction)
    this.camera.position.set(
      this.camera.position.x += direction.x * this.flySpeed,
      this.camera.position.y += direction.y * this.flySpeed,
      this.camera.position.z += direction.z * this.flySpeed
    )
    this.refreshLightPosition()

    // Speed
    const ratio = this.cameraRotation.x / Math.PI
    if(ratio < 0){
      // Speed up
      let angleMapSpeed = this.flyMaxSpeed * ((-ratio) * 2)
      angleMapSpeed += this.flyMaxSpeed /2
      this.flySpeed += (angleMapSpeed - this.flySpeed) * 0.01
    }
    else{
      this.flySpeed += (0 - this.flySpeed) * (0.01 * ratio)
    }

    this.flyValue.innerText = Math.round((this.flySpeed / this.flyMaxSpeed) * 100) / 100

    // Animate Ground
    const stepX = 1.8
    const stepY = 1

    const newX = stepX * Math.round(this.camera.position.x / stepX)
    const newZ = stepY * Math.round(this.camera.position.z / stepY)

    this.uniforms.uOffsetX.value = newX
    this.uniforms.uOffsetZ.value = newZ

    this.water.position.x = newX
    this.water.position.z = newZ
  }

  setSky(){
    const sky = new THREE.Mesh(
      new THREE.IcosahedronGeometry(10000, 1),
      new THREE.MeshBasicMaterial({
        wireframe: true,
        transparent: true,
        opacity: 0.05
      })
    )
    this.scene.add( sky )
  }
}